import { datadogRum } from '@datadog/browser-rum';
import { POST } from '@carvana/core-http';
import { getUserState } from '@carvana/core-auth';
import { SarStore } from '@store/sar-store';
import { CORRELATION_ID_HEADER, getOrCreateSessionId } from '@utils/session-id';
import { getApiBaseUrl } from '../base-url-config';

type AddressGroup = {
  streetAddress?: string;
  city?: string;
  state?: string;
  zip5?: string;
};

type FetchRepairFacilitiesBody = {
  address?: AddressGroup;
  category?: string;
  subcategory?: string;
  subsubscategory?: string;
  asrId?: number | null;
  userId?: string;
  sarSessionId?: string;
  coverageScreen?: string;
  source?: string;
};

export type RepairFacility = {
  vendorId: number;
  name: string;
  rating: string;
  distanceFromLocation: string;
  street: string;
  street2?: string;
  city: string;
  state: string;
  zip5: string;
  phoneNumber: string;
  website: string;
  url: string;
  isMobile: boolean;
  isNational: boolean;
};

interface RepairFacilityApiContent {
  facilities: RepairFacility[];
  experimentName: string | null;
  bucket: string | null;
}

interface RepairFacilityResponse {
  content: RepairFacilityApiContent | RepairFacility[];
}

export type RepairFacilitiesResult = {
  facilities: RepairFacility[];
  experimentName: string | null;
  bucket: string | null;
};

export const RepairFacilities = {
  fetchFacilities: async (userAddress, userSelections, asrId?: number | null): Promise<RepairFacilitiesResult | null> => {
    const ddAction = 'fetch-repair-locations';

    const startTime = Date.now();

    try {
      datadogRum.addAction(ddAction, { type: 'start', sarSessionId: SarStore.sarSessionId, asrId: asrId ?? null });

      const { addressGroup } = userAddress || {};
      const formattedAddress = {
        street: addressGroup?.streetAddress || '',
        city: addressGroup?.city || '',
        state: addressGroup?.state || '',
        zip5: addressGroup?.zip5 || '',
      };

      const body: FetchRepairFacilitiesBody = {
        ...formattedAddress,
        category: userSelections?.category,
        subcategory: userSelections?.subcategory || userSelections?.category,
        subsubscategory: userSelections?.subsubcategory,
        asrId: asrId ?? null,
        userId: getUserState()?.user?.user_id || '',
        sarSessionId: SarStore.sarSessionId || '',
        coverageScreen: SarStore.coverageScreenType || '',
        source: 'my_cars',
      };

      const url = `${getApiBaseUrl()}/core/mycars/api/v1/repair-locations`;
      const response = await POST(url, body, {
        authorized: true,
        headers: { [CORRELATION_ID_HEADER]: getOrCreateSessionId() },
        timeout: 15000,
      });

      const data: RepairFacilityResponse = await response.json();

      if (!response.ok) {
        datadogRum.addAction(ddAction, { type: 'error', error: data, sarSessionId: SarStore.sarSessionId, asrId: asrId ?? null, elapsedMs: Date.now() - startTime });
        return null;
      }

      datadogRum.addAction(ddAction, { type: 'success', sarSessionId: SarStore.sarSessionId, asrId: asrId ?? null, elapsedMs: Date.now() - startTime });
      const content = data.content;
      // Handle both old (content is array) and new (content is object with facilities) API shapes
      const facilities = Array.isArray(content) ? content : content?.facilities || [];
      const experimentName = Array.isArray(content) ? null : content?.experimentName || null;
      const bucket = Array.isArray(content) ? null : content?.bucket || null;
      return { facilities, experimentName, bucket };
    } catch (e) {
      datadogRum.addAction(ddAction, { type: 'error', error: e, sarSessionId: SarStore.sarSessionId, asrId: asrId ?? null, elapsedMs: Date.now() - startTime });
      return null;
    }
  },
};

export default RepairFacilities;
